import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'

import { ApiError, setFallbackMessage, setUnauthorizedHandler } from '@/api/client'
import { keys } from '@/api/keys'
import { ToastProvider } from '@/components/ui/Toast'
import { useSyncAccountLanguage } from '@/features/auth'
import { useT } from '@/i18n'

function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // 4xx 不重试：重试也不会变好
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false
          return failureCount < 2
        },
      },
      mutations: { retry: false },
    },
  })
}

/** 只为副作用存在：要在 QueryClientProvider 里面才能读到登录态 */
function AccountLanguageSync() {
  useSyncAccountLanguage()
  return null
}

/** api/client 不在 React 树里，兜底文案只能注册进去 */
function ApiMessages() {
  const t = useT()
  useEffect(() => {
    setFallbackMessage(t('toast.requestFailed'))
  }, [t])
  return null
}

export function Providers({ children }: { children: ReactNode }) {
  const [client] = useState(createQueryClient)

  // 刷新失败：清空登录态，RequireAuth 会把用户送去登录页
  useEffect(() => {
    setUnauthorizedHandler(() => {
      client.setQueryData(keys.me(), null)
    })
  }, [client])

  return (
    <QueryClientProvider client={client}>
      <AccountLanguageSync />
      <ApiMessages />
      <ToastProvider>{children}</ToastProvider>
    </QueryClientProvider>
  )
}
